import { Trans } from '@lingui/react/macro';
import { Box, ListItem } from '@mui/material';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
// @ts-expect-error TS(7016): Could not find a declaration file for module 'reac... Remove this comment to see the full error message
import { Flags } from 'react-feature-flags';
import { useSelector } from 'react-redux';

import { VideaTypography } from '../../../../../common-components/elements/VideaTypography/VideaTypography';
import {
  AREA_OF_CONCERN,
  CALCULUS,
  CARIES,
  RADIOGRAPHIC_BONELOSS,
  SHOW_VALIDATION_DROP_DOWN_MENUS_RULE_NAME,
} from '../../../../../constants';
import { usePreferences } from '../../../../../react-query/usePreferences';
import {
  greyLight700,
  purple050,
  white,
} from '../../../../../theme/videa-style-tokens';
import { useUniversalToFDI } from '../../../../../utils/internationalization/useUniversalToFDI';
import {
  computeBoneLossMeasurements,
  isModerateOrSevereRblFinding,
  useGetSideToShowAndMeasurementToDisplay,
} from '../../../../../utils/rbl';
import FindingValidationSelector from '../../../findingValidationSelector/FindingValidationSelector';
import ValidationDropdown from '../../../validationDropdown/ValidationDropdown';

import { useGetPathologyNameToDisplay } from './utils';

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- 'theme' is defined but never used.
const useStyles = makeStyles((theme) => ({
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'column',
    alignItems: 'center',
    color: white,
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.1)',
      '& $percent ': {
        color: 'red !important',
        fontWeight: 'bold',
      },
    },
    '&.MuiListItem-root': {
      '&:hover': {
        backgroundColor: 'rgba(255,255,255,0.1)',
      },
    },
    padding: '0.5em 1em',
  },
  listHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 0,
  },
  percent: {
    color: purple050,
  },
  commonConfidence: {
    color: white,
    borderRadius: '.375em',
    textAlign: 'center',
    width: 'auto',
    justifySelf: 'flex-start',
    fontFamily: 'proxima-nova, sans-serif',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '.875em',
    lineHeight: '1.25em',
    display: 'flex',
    paddingTop: '.375em',
  },
  toothNumberText: {
    fontFamily: 'proxima-nova, sans-serif',
    fontWeight: 500,
  },
  findings: {
    paddingLeft: '.625em',
  },
  image: {
    color: greyLight700,
    marginLeft: 'auto',
  },
  cariesGroup: {
    flexDirection: 'column',
  },
  iconRow: {
    display: 'flex',
    flexDirection: 'row',
  },
  icon: {
    cursor: 'pointer',
  },
  cancelIcon: {
    '&selected, &:hover': {
      fill: 'red',
    },
  },
  confirmIcon: {
    '&selected, &:hover': {
      fill: 'green',
    },
  },
  watchIcon: {
    '&selected, &:hover': {
      fill: 'orange',
    },
  },
  iconCol: {
    flexDirection: 'column',
    display: 'flex',
    padding: '.625em',
    alignItems: 'center',
  },
  divider: {
    borderRight: `1px solid ${greyLight700}`,
    marginRight: '.375em',
    marginLeft: '.375em',
    height: '32px',
    alignSelf: 'center',
  },
  caption: {
    fontFamily: 'proxima-nova, sans-serif',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '.625em',
    lineHeight: '.875em',
    paddingTop: '.375em',
  },
}));

const PathologyItem = (props: any) => {
  const {
    finding = {},
    findingsForTooth = [],
    onFindingMouseOver = () => {},
    onFindingMouseLeave = () => {},
    updateFinding = () => {},
    setAnchorEls = {},
    onDeleteClick = () => {},
    isPAImage = false,
  } = props;

  const practiceThreshold = useSelector(
    (state: any) => state.login.user.practice?.rblSettings?.templateName,
  );
  const classes = useStyles();

  const preferencesQuery = usePreferences();
  const { displayToothNumber } = useUniversalToFDI();
  const getPathologyNameToDisplay = useGetPathologyNameToDisplay();

  const onValidationReasonChange = (event: any) => {
    const { value } = event.target;
    const { validationReasons = [] } = finding;
    const activeReason = validationReasons.find(
      (reason: any) => reason === value,
    );
    const newValidationReasons = activeReason
      ? validationReasons.filter((reason: any) => reason !== value)
      : [...validationReasons, value];

    updateFinding(finding, '', '', newValidationReasons);
  };

  const shouldShowMesial = finding?.side === 'mesial';

  let mesialMeasurement: any = null;
  let distalMeasurement: any = null;
  let unknown1Measurement = null;
  let unknown2Measurement = null;

  if (finding.type === RADIOGRAPHIC_BONELOSS) {
    const bonelossMeasurements = computeBoneLossMeasurements(
      findingsForTooth,
      isPAImage,
      preferencesQuery.data?.enableLatestImagesV2,
    );
    mesialMeasurement = bonelossMeasurements.mesialMeasurement;
    distalMeasurement = bonelossMeasurements.distalMeasurement;
    unknown1Measurement = bonelossMeasurements.unknown1Measurement;
    unknown2Measurement = bonelossMeasurements.unknown2Measurement;
  }

  const willValidationSelectorShow = (findingType: any) => {
    if (findingType === CALCULUS) {
      return false;
    }
    if (findingType === RADIOGRAPHIC_BONELOSS) {
      return shouldShowMesial
        ? isModerateOrSevereRblFinding(
            mesialMeasurement,
            isPAImage,
            practiceThreshold,
          )
        : isModerateOrSevereRblFinding(
            distalMeasurement,
            isPAImage,
            practiceThreshold,
          );
    }
    return true;
  };

  const { text, measurement } = useGetSideToShowAndMeasurementToDisplay({
    finding,
    distalMeasurement,
    isPAImage,
    mesialMeasurement,
    unknown1Measurement,
    unknown2Measurement,
  });

  const isCariesOrAreaOfConcern =
    finding?.type === CARIES || finding?.type === AREA_OF_CONCERN;

  const shouldDisplayToothNumber = finding.type !== RADIOGRAPHIC_BONELOSS;

  return (
    <ListItem
      data-cy={finding?.source !== 'AI' ? 'userAnnotation' : finding?.type}
      className={classes.listItem}
      onMouseOver={() => onFindingMouseOver(finding)}
      onMouseLeave={() => onFindingMouseLeave(finding)}
    >
      {shouldDisplayToothNumber && finding?.source !== 'userAnnotations' && (
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width={1}
          marginBottom={2}
        >
          {finding.toothNumber ? (
            <VideaTypography
              variant="labelSmall"
              className={classes.toothNumberText}
            >
              <Trans>Tooth {displayToothNumber(finding.toothNumber)}</Trans>
            </VideaTypography>
          ) : (
            <VideaTypography
              variant="bodyExtrasmall"
              className={classes.toothNumberText}
            >
              <Trans>Tooth # unknown</Trans>
            </VideaTypography>
          )}
        </Box>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={isCariesOrAreaOfConcern ? 'start' : 'center'}
        width="100%"
      >
        <VideaTypography
          variant="bodySmall"
          className={classes.commonConfidence}
        >
          {getPathologyNameToDisplay(finding?.type, { text, measurement })}
        </VideaTypography>

        <Flags authorizedFlags={[SHOW_VALIDATION_DROP_DOWN_MENUS_RULE_NAME]}>
          {(finding.hover ||
            (finding.validation === 'Agree' &&
              finding.validationReasons?.length > 0)) && (
            <ValidationDropdown
              finding={finding}
              onValidationReasonChange={onValidationReasonChange}
            />
          )}
        </Flags>
        <Box
          display="flex"
          flexDirection="row"
          alignItems="end"
          width="auto"
          marginTop="3px"
          className={classes.cariesGroup}
        >
          {willValidationSelectorShow(finding.type) && (
            <FindingValidationSelector
              finding={finding}
              findingGroupType={finding.type}
              isFullScreenViewer={false}
              updateFinding={updateFinding}
              setAnchorEls={setAnchorEls}
              onDeleteClick={onDeleteClick}
            />
          )}
        </Box>
      </Box>
    </ListItem>
  );
};

PathologyItem.propTypes = {
  finding: PropTypes.object,
  findingsForTooth: PropTypes.array,
  onFindingMouseOver: PropTypes.func,
  onFindingMouseLeave: PropTypes.func,
  updateFinding: PropTypes.func,
  onDeleteClick: PropTypes.func,
  setAnchorEls: PropTypes.object,
  isPAImage: PropTypes.bool,
};

export default PathologyItem;
